import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Clock, ArrowRight, Folder, Trash2, Video, Loader2, FileSpreadsheet, Zap, Sparkles } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Projects = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewProject, setShowNewProject] = useState(false);
  const [showBatchOptions, setShowBatchOptions] = useState(false);
  const [projectName, setProjectName] = useState("");
  const [creating, setCreating] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (location.state?.openNewProject) {
      setShowNewProject(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`, { withCredentials: true });
      setProjects(response.data.projects || []);
    } catch (error) {
      console.error("Error fetching projects:", error);
      if (error.response?.status === 401) {
        navigate('/login');
      } else {
        toast.error("Failed to load projects");
      }
    } finally {
      setLoading(false);
    }
  };

  const createProject = async () => {
    if (!projectName.trim()) {
      toast.error("Please enter a project name");
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API}/projects`, { name: projectName }, { withCredentials: true });
      const project = response.data.project;
      toast.success("Project created!");
      navigate(`/project/${project.id}`);
    } catch (error) {
      console.error("Error creating project:", error);
      toast.error("Failed to create project");
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteClick = (e, project) => {
    e.stopPropagation(); // Prevent navigating to project
    setProjectToDelete(project);
    setDeleteDialogOpen(true);
  };

  const deleteProject = async () => {
    if (!projectToDelete) return;

    setDeleting(true);
    try {
      await axios.delete(`${API}/projects/${projectToDelete.id}`, { withCredentials: true });
      toast.success("Project deleted successfully");
      setProjects(projects.filter(p => p.id !== projectToDelete.id));
      setDeleteDialogOpen(false);
      setProjectToDelete(null);
    } catch (error) {
      console.error("Error deleting project:", error);
      toast.error("Failed to delete project");
    } finally {
      setDeleting(false);
    }
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      created: "Draft",
      analyzed: "Analyzed",
      concepts_generated: "Concepts Ready",
      script_generated: "Script Ready",
      anchor_images_generated: "Images Ready",
      videos_generated: "Videos Ready",
      completed: "Completed"
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status) => {
    if (status === "completed") return "bg-green-100 text-green-700";
    if (status === "created") return "bg-gray-100 text-gray-700";
    return "bg-blue-100 text-blue-700";
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="font-display text-xl font-semibold">Dashboard</h1>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              onClick={() => setShowNewProject(true)}
              className="rounded-full px-6 bg-primary hover:opacity-90 gap-2"
              data-testid="new-project-btn"
            >
              <Plus className="w-4 h-4" strokeWidth={1.5} />
              New Project
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="font-display text-3xl font-semibold mb-2">Your Projects</h1>
          <p className="text-muted-foreground">Create and manage your video ad campaigns</p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-xl border border-border p-6 animate-pulse">
                <div className="h-6 bg-muted rounded w-2/3 mb-4"></div>
                <div className="h-4 bg-muted rounded w-1/2 mb-2"></div>
                <div className="h-4 bg-muted rounded w-1/3"></div>
              </div>
            ))}
          </div>
        ) : projects.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <div className="w-16 h-16 bg-secondary rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Folder className="w-8 h-8 text-muted-foreground" strokeWidth={1.5} />
            </div>
            <h2 className="font-display text-xl font-semibold mb-2">No projects yet</h2>
            <p className="text-muted-foreground mb-6">Create your first video ad project to get started</p>
            <Button
              onClick={() => setShowNewProject(true)}
              className="rounded-full px-8 py-6 bg-primary hover:opacity-90 gap-2"
              data-testid="empty-state-new-project-btn"
            >
              <Plus className="w-4 h-4" strokeWidth={1.5} />
              Create Project
            </Button>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl border border-border p-6 hover:shadow-medium transition-shadow cursor-pointer group relative"
                onClick={() => navigate(`/project/${project.id}`)}
                data-testid={`project-card-${project.id}`}
              >
                {/* Delete Button */}
                <button
                  onClick={(e) => handleDeleteClick(e, project)}
                  className="absolute top-4 right-4 p-2 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-red-50 text-muted-foreground hover:text-red-600 transition-all"
                  data-testid={`delete-project-${project.id}`}
                  title="Delete project"
                >
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>

                <div className="flex items-start justify-between mb-4 pr-8">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Video className="w-5 h-5 text-primary" strokeWidth={1.5} />
                  </div>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${getStatusColor(project.status)}`}>
                    {getStatusLabel(project.status)}
                  </span>
                </div>
                <h3 className="font-display text-lg font-semibold mb-2 group-hover:text-primary transition-colors">
                  {project.name}
                </h3>
                {project.product_analysis?.product_name && (
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-1">
                    {project.product_analysis.product_name}
                  </p>
                )}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="w-4 h-4" strokeWidth={1.5} />
                    <span>
                      {new Date(project.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" strokeWidth={1.5} />
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>

      {/* New Project Dialog */}
      <Dialog open={showNewProject} onOpenChange={setShowNewProject}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Create New Project</DialogTitle>
            <DialogDescription>
              Start a new video ad campaign by giving it a name
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="e.g., Summer Sale Campaign"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              className="h-12 rounded-lg"
              data-testid="project-name-input"
              onKeyDown={(e) => e.key === 'Enter' && createProject()}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowNewProject(false)}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              onClick={createProject}
              disabled={creating || !projectName.trim()}
              className="rounded-full bg-primary hover:opacity-90"
              data-testid="create-project-submit-btn"
            >
              {creating ? "Creating..." : "Create Project"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Delete Project</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <span className="font-semibold text-foreground">"{projectToDelete?.name}"</span>? 
              This action cannot be undone. All generated images, videos, and audio files will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              className="rounded-full"
              disabled={deleting}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteProject}
              disabled={deleting}
              className="rounded-full bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-btn"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete Project"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Batch Processing Options Dialog */}
      <Dialog open={showBatchOptions} onOpenChange={setShowBatchOptions}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Choose Batch Processing Type</DialogTitle>
            <DialogDescription>
              Select the type of batch processing for your video ads
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            {/* 20 Seconds Video - Original */}
            <button
              onClick={() => {
                setShowBatchOptions(false);
                navigate('/shopsy-batch');
              }}
              className="w-full p-4 rounded-xl border border-border hover:border-primary hover:bg-primary/5 transition-all text-left group"
              data-testid="batch-option-shopsy"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <FileSpreadsheet className="w-6 h-6 text-blue-600" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                    20 Seconds Video
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Original batch processing with Gemini for image generation. Best for standard quality videos.
                  </p>
                </div>
                <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors mt-1" />
              </div>
            </button>

            {/* 10 Seconds Video - Fast */}
            <button
              onClick={() => {
                setShowBatchOptions(false);
                navigate('/shopsy-batch-10s');
              }}
              className="w-full p-4 rounded-xl border border-border hover:border-green-500 hover:bg-green-50 transition-all text-left group"
              data-testid="batch-option-10s"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Zap className="w-6 h-6 text-green-600" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground group-hover:text-green-600 transition-colors">
                    10 Seconds Video
                    <span className="ml-2 text-xs font-medium px-2 py-0.5 bg-green-100 text-green-700 rounded-full">Fast</span>
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Quick 10-second video ads. Perfect for high-volume campaigns with faster turnaround.
                  </p>
                </div>
                <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-green-500 transition-colors mt-1" />
              </div>
            </button>

            {/* Batch V3 - Fal.ai */}
            <button
              onClick={() => {
                setShowBatchOptions(false);
                navigate('/shopsy-batch-v3');
              }}
              className="w-full p-4 rounded-xl border border-border hover:border-purple-500 hover:bg-purple-50 transition-all text-left group"
              data-testid="batch-option-v3"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-6 h-6 text-purple-600" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground group-hover:text-purple-600 transition-colors">
                    Batch V3
                    <span className="ml-2 text-xs font-medium px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">Fal.ai</span>
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Uses Fal.ai Nano Banana for anchor image generation. Higher quality visuals with advanced AI.
                  </p>
                </div>
                <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-purple-500 transition-colors mt-1" />
              </div>
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Projects;
